import { useTranslation } from 'react-i18next';
import { ReactElement, useEffect, useState, useMemo } from 'react';
import { DrawerPanel, ProgressIndicatorCircular } from '@dmc/ui';
import { CasFormFilePicker } from '@cas/components/inputs/cas-form-file-picker/CasFormFilePicker';
import { ModelName, QueryTag } from '@casops/store/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import { FileUploadModel } from '@casops/store/apis/commonModels';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useFileUploadForm from '@casops/components/layout/model-form/useFileUploadForm';
import { CasFormDateTimePicker } from '@casops/components/inputs/cas-form-date-time-picker/CasFormDateTimePicker';
import { useParams } from 'react-router-dom';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import {
  ConfigPackageFileType,
  ConfigurationGroupPackageFormModel,
} from '@casops/store/apis/configurationGroupsApi';
import { useWatch } from 'react-hook-form';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { LibraryItemModel } from '@casops/store/apis/libraryApi';
import CasFormConditionalDisplay from '@cas/components/inputs/cas-form-conditional-display/CasFormConditionalDisplay';
import { LibraryItemTypeModel } from '@casops/store/apis/settingsApi';
import useConfigurationGroupPackage from './useConfigurationGroupPackage';
import { MINUS_ONE } from '@casops/shared/constants';
import { ServerErrorFields } from '@dmc/ui';

type FormModel = ConfigurationGroupPackageFormModel &
  FileUploadModel & {
    libid: LibraryItemModel['id'];
    softwareFirmware: LibraryItemTypeModel['id'];
  };

const modelName = ModelName.ConfigurationGroupPackage;

const initialValues: FormModel = {
  ty: null,
  at: new Date().toISOString(),
  file: undefined,
  libid: MINUS_ONE,
  softwareFirmware: MINUS_ONE,
  name: '',
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  name: 'Name',
};

export function ManageConfigurationGroupPackage(): ReactElement {
  const { t } = useTranslation();
  const { configGroupId } = useParams();
  const endpointUrl = `api/configurationgroups/${configGroupId}/packages`;
  const tagsToClear: TagDescription<QueryTag>[] = [
    { type: QueryTag.ConfigurationGroup, id: configGroupId },
  ];
  const [formType, setFormType] = useState<FormModel['ty']>(initialValues.ty);
  const [libraryTypeId, setLibraryTypeId] = useState<LibraryItemTypeModel['id']>(
    initialValues.softwareFirmware,
  );
  const showVersionDrop = libraryTypeId > MINUS_ONE;
  const isCustomFormType = formType === ConfigPackageFileType.Custom;
  const isLibraryFormType = formType === ConfigPackageFileType.Library;
  const isSettingsFormType = formType === ConfigPackageFileType.Settings;
  const isFormTypeSelected = formType !== null;
  const acceptedFileFormats = useMemo(() => {
    if (isSettingsFormType) {
      return '.gz,.tgz,.tar.gz';
    } else if (isCustomFormType) {
      return '.gz,.tgz,.tar.gz,.apk,.bin';
    }
    return '';
  }, [isCustomFormType, isSettingsFormType]);
  const {
    schema,
    libraryTypesOptions,
    libraryVersionOptions,
    configPackageFileTypeLabel,
    dftValuesLibraryTypes,
  } = useConfigurationGroupPackage({
    isCustomFormType,
    isLibraryFormType,
    libraryTypeId,
  });

  const formProps = useFileUploadForm<FormModel>({
    endpointUrl,
    tagsToClear,
    defaultValues: initialValues,
    successMessage: t(`Model.Create.Success`, { modelName }),
    fieldsSchema: schema,
    isFileUpload: !isLibraryFormType,
    validateOnLoad: true,
    serverErrorFields,
  });
  const watchedPackageType = useWatch({
    control: formProps.control,
    name: 'ty',
  });
  const watchedSoftwareFirmware = useWatch({
    control: formProps.control,
    name: 'softwareFirmware',
  });

  useEffect(() => {
    setFormType(watchedPackageType);
  }, [watchedPackageType]);

  useEffect(() => {
    setLibraryTypeId(watchedSoftwareFirmware);
    formProps.setValue('libid', -1, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSoftwareFirmware]);

  useEffect(() => {
    if (
      isLibraryFormType &&
      dftValuesLibraryTypes &&
      libraryTypeId > MINUS_ONE &&
      typeof dftValuesLibraryTypes[libraryTypeId]
    ) {
      const defaultName = dftValuesLibraryTypes[libraryTypeId];
      formProps.setValue('name', defaultName, { shouldValidate: true });
    } else if (
      isCustomFormType ||
      isSettingsFormType ||
      (isLibraryFormType && libraryTypeId === MINUS_ONE)
    ) {
      formProps.setValue('name', '', { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, libraryTypeId]);

  return (
    <DrawerPanel header={t('Model.Create.Title', { modelName })}>
      <ModelForm {...formProps} submitLabel={t('Create')}>
        <CasFormDropdown
          control={formProps.control}
          name="ty"
          label={t('Fields.PackageType')}
          items={configPackageFileTypeLabel}
          placeholder={t('Select')}
        />
        <CasFormConditionalDisplay
          show={isFormTypeSelected && isLibraryFormType}
          name="softwareFirmware"
          resetField={formProps.resetField}
        >
          <CasFormDropdown
            control={formProps.control}
            name="softwareFirmware"
            label={t('Fields.SoftwareFirmware')}
            items={libraryTypesOptions}
            placeholder={libraryTypesOptions.length === 0 ? t('NoneAvailable') : ''}
          />
        </CasFormConditionalDisplay>
        <CasFormConditionalDisplay
          show={isFormTypeSelected && isLibraryFormType && showVersionDrop}
          name="libid"
          resetField={formProps.resetField}
        >
          <CasFormDropdown
            control={formProps.control}
            name="libid"
            label={t('Fields.Version')}
            items={libraryVersionOptions}
          />
        </CasFormConditionalDisplay>
        <CasFormConditionalDisplay
          show={isFormTypeSelected}
          name="name"
          resetField={formProps.resetField}
        >
          <CasFormInput control={formProps.control} name="name" label={t('Fields.PackageName')} />
        </CasFormConditionalDisplay>
        <CasFormConditionalDisplay
          show={isFormTypeSelected && isCustomFormType}
          name="ver"
          resetField={formProps.resetField}
        >
          <CasFormInput control={formProps.control} name="ver" label={t('Fields.Version')} />
        </CasFormConditionalDisplay>
        <CasFormConditionalDisplay
          show={isFormTypeSelected}
          name="at"
          resetField={formProps.resetField}
        >
          <CasFormDateTimePicker key="at" control={formProps.control} name="at" />
        </CasFormConditionalDisplay>
        <CasFormConditionalDisplay
          show={isFormTypeSelected && !isLibraryFormType}
          name="file"
          resetField={formProps.resetField}
        >
          <CasFormFilePicker
            control={formProps.control}
            name="file"
            accept={acceptedFileFormats}
            minHeight="200px"
          />
        </CasFormConditionalDisplay>
        {formProps.percentage > 0 && formProps.percentage < 100 && (
          <div style={{ marginBottom: 16 }}>
            <ProgressIndicatorCircular small />
            <span>{t('Fields.Uploading', { percentage: formProps.percentage })}</span>
          </div>
        )}
      </ModelForm>
    </DrawerPanel>
  );
}

export default ManageConfigurationGroupPackage;
